import { useQuery } from '@apollo/client';
import { type PropsWithChildren as PWC } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';

import { Loader } from '../components/common/Loader';
import { getFragment } from '../graphql/client/helpers/get-fragment';
import { GetPrescriberOrderDocument } from '../graphql/generated/graphql';
import { useHasEnhancedFlow } from '../hooks/use-has-enhanced-flow';
import { useAuthorizedMedication } from '../hooks/use-order-medication';
import { isRoutable } from '../util';
import { getAbsoluteRoute } from '../util/get-absolute-route';

import {
    PrescriberOrderContext,
    type PrescriberOrderCtx,
} from './prescriber-order-context';

export const PrescriberOrderProvider = ({ children }: PWC) => {
    const { prescriberOrderId } = useParams();
    const { isAuthenticated } = useAuthContext();

    const { called, data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId: prescriberOrderId! },
        skip: !prescriberOrderId || !isAuthenticated,
    });

    const prescriberOrder = getFragment(data?.prescriberOrderV2);
    const medication = useAuthorizedMedication(prescriberOrder);

    const hasEnhancedFlow = useHasEnhancedFlow(prescriberOrder);

    if (!prescriberOrderId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (loading || !called || !isAuthenticated) {
        return <Loader />;
    }

    if (
        !prescriberOrder ||
        // The medication doesn't exist if this is an unclaimed script, but is mandatory otherwise
        (!medication &&
            prescriberOrder.__typename !== 'UnclaimedPrescriberOrder')
    ) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    // Claimed, unrouted scripts might go through the new flow
    if (hasEnhancedFlow && isRoutable(prescriberOrder)) {
        return (
            <Navigate
                replace
                to={getAbsoluteRoute('fill.drxId.root', {
                    drxId: prescriberOrderId!,
                })}
            />
        );
    }

    const ctx: PrescriberOrderCtx = {
        medication,
        order: prescriberOrder,
        orderId: prescriberOrderId!,
    };

    return (
        <PrescriberOrderContext.Provider value={ctx}>
            {children}
        </PrescriberOrderContext.Provider>
    );
};
